import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency, formatDate, projectTypeLabels } from "@/lib/format";
import { Check, Send } from "lucide-react";
import { respondToProposal } from "@/lib/proposal-accept.functions";

export const Route = createFileRoute("/p/$slug")({
  head: () => ({
    meta: [
      { title: "Your proposal — Wealthy Creatives Boardroom" },
      { name: "description", content: "Review and accept your proposal from a Wealthy Creatives member." },
    ],
  }),
  component: ProposalPage,
});

type SharedProposal = {
  proposal_id: string;
  client_name: string;
  project_type: string;
  value_cents: number;
  notes: string | null;
  stage: string;
  sent_date: string | null;
  member_name: string | null;
  member_avatar: string | null;
  member_identity: string | null;
};

function ProposalPage() {
  const { slug } = Route.useParams();
  const [data, setData] = useState<SharedProposal | null | undefined>(undefined);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<false | "accepted" | "replied">(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Fetch the shared proposal via the public RPC.
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data: rpcData, error } = await supabase.rpc("get_shared_proposal", { _slug: slug });
      if (!alive) return;
      if (error || !rpcData || rpcData.length === 0) {
        setData(null);
        return;
      }
      setData(rpcData[0] as SharedProposal);
    })();
    return () => { alive = false; };
  }, [slug]);

  if (data === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1419] text-muted-foreground">
        Loading proposal…
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1419] text-center px-6">
        <div>
          <h1 className="font-display text-2xl font-semibold mb-2">Proposal not available</h1>
          <p className="text-muted-foreground text-sm">
            This link may have expired or the proposal hasn't been shared yet. Reach out to your contact directly.
          </p>
        </div>
      </div>
    );
  }

  const memberName = data.member_name || "Your creative partner";
  const memberIdentity = data.member_identity;
  const initials = (memberName || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  async function submit(accept: boolean) {
    if (!name.trim() || !email.trim()) {
      setFormError("Please add your name and email.");
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await respondToProposal({
        data: { slug, name: name.trim(), email: email.trim(), message: message.trim() || null, accept },
      });
      if (!res.ok) { setFormError(res.error); return; }
      setDone(accept ? "accepted" : "replied");
    } catch {
      setFormError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    void submit(true);
  }

  return (
    <div className="min-h-screen bg-[#0F1419] text-white">
      <div className="max-w-2xl mx-auto px-5 py-10 sm:py-16">
        {/* Member branding header */}
        <div className="flex items-center gap-4 mb-10">
          {data.member_avatar ? (
            <img
              src={data.member_avatar}
              alt={memberName}
              className="h-14 w-14 rounded-full object-cover ring-2 ring-[color:var(--gold-dim)]"
            />
          ) : (
            <div className="h-14 w-14 rounded-full bg-[color:var(--gold)] text-black flex items-center justify-center font-display font-semibold text-lg">
              {initials}
            </div>
          )}
          <div>
            <div className="font-display text-xl font-semibold">{memberName}</div>
            {memberIdentity && (
              <div className="text-sm text-[color:var(--gold-dim)]">{memberIdentity}</div>
            )}
          </div>
        </div>

        {/* Proposal */}
        <div className="boardroom-card p-6 sm:p-8">
          <div className="text-[11px] uppercase tracking-wider text-[color:var(--gold-dim)] mb-1">
            Proposal for {data.client_name}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold mb-4">
            {projectTypeLabels[data.project_type] ?? "Project"} — {formatCurrency(data.value_cents)}
          </h1>

          {data.sent_date && (
            <div className="text-sm text-muted-foreground mb-4">
              Offer given {formatDate(data.sent_date)}
            </div>
          )}

          {data.notes && (
            <div className="border-t border-[color:var(--hairline)] pt-4 mt-4">
              <div className="prose prose-invert max-w-none text-sm leading-relaxed whitespace-pre-wrap">
                {data.notes}
              </div>
            </div>
          )}

          <div className="border-t border-[color:var(--hairline)] mt-6 pt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="h-4 w-4 text-[color:var(--success)]" />
            Ready to move forward? Send a note back below.
          </div>
        </div>

        {/* Response form */}
        <div className="boardroom-card p-6 sm:p-8 mt-6">
          {done ? (
            <div className="text-center py-6">
              <div className="h-12 w-12 rounded-full bg-[color:var(--success)]/20 text-[color:var(--success)] flex items-center justify-center mx-auto mb-3">
                <Check className="h-6 w-6" />
              </div>
              <h2 className="font-display text-xl font-semibold mb-1">
                {done === "accepted" ? "Proposal accepted" : "Message sent"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {done === "accepted"
                  ? `${memberName.split(" ")[0]} has been notified and will reach out with next steps and an invoice.`
                  : `${memberName.split(" ")[0]} will get back to you shortly. Thank you!`}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h2 className="font-display text-lg font-semibold">Accept this proposal</h2>
              <p className="text-sm text-muted-foreground -mt-2">
                Add your name and email. {memberName.split(" ")[0]} will be notified right away.
              </p>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Your name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-1 w-full rounded-md bg-[color:var(--surface-2)] border border-[color:var(--hairline)] px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--gold-dim)]"
                  placeholder="First and last name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 w-full rounded-md bg-[color:var(--surface-2)] border border-[color:var(--hairline)] px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--gold-dim)]"
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Message (optional)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-md bg-[color:var(--surface-2)] border border-[color:var(--hairline)] px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--gold-dim)]"
                  placeholder="I'm ready to move forward, or I have a question about…"
                />
              </div>
              {formError && <p className="text-sm text-destructive">{formError}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[color:var(--gold)] text-black font-medium py-2.5 text-sm hover:opacity-90 disabled:opacity-50"
              >
                <Check className="h-4 w-4" /> {submitting ? "Sending…" : "Accept proposal"}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => void submit(false)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-[color:var(--hairline)] py-2.5 text-sm hover:border-[color:var(--gold-dim)] disabled:opacity-50"
              >
                <Send className="h-4 w-4" /> Just ask a question
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-[10px] text-muted-foreground mt-6">
          Powered by The Wealthy Creatives Boardroom
        </p>
      </div>
    </div>
  );
}
